// Helpers for the private /med page.
// - Time math (HH:MM strings, 5.5h cascade)
// - Current Eastern Time (America/New_York) date + time
// - Ideal-time computation for the four slots
// - Tiny API client

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// Slots
// -----------------------------------------------------------------------------
export const SLOTS = [
  { key: 'morning', label: 'Morning Meds' },
  { key: 'afternoon', label: 'Afternoon Buspar' },
  { key: 'night', label: 'Night Time Buspar' },
  { key: 'bedtime', label: 'Bedtime Meds' },
];

// Fixed ideal times (24h).
export const MORNING_IDEAL = '13:00';   // 1:00 PM
export const BEDTIME_IDEAL = '02:00';   // 2:00 AM
export const CASCADE_MINUTES = 330;      // 5.5 hours

export function emptyDay(date) {
  return {
    date,
    note: '',
    morning:   { checked: false, taken_time: null, ideal_time: null },
    afternoon: { checked: false, taken_time: null, ideal_time: null },
    night:     { checked: false, taken_time: null, ideal_time: null },
    bedtime:   { checked: false, taken_time: null, ideal_time: null },
  };
}

// -----------------------------------------------------------------------------
// Time helpers
// -----------------------------------------------------------------------------
export function parseHHMM(s) {
  if (typeof s !== 'string' || !/^\d{2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(':').map(Number);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

export function fmtHHMM(mins) {
  const total = ((Math.round(mins) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// Format "HH:MM" (24h) → "h:mm AM/PM"
export function fmt12(hhmm) {
  const mins = parseHHMM(hhmm);
  if (mins == null) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// Ultra-compact 12h format for cramped spots (calendar cells).
// - Drops minutes when they're :00 (1p, 12a, 2a)
// - Uses lowercase a/p to save a character (1:07p, 12:07a)
export function fmt12Short(hhmm) {
  const mins = parseHHMM(hhmm);
  if (mins == null) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const suffix = h >= 12 ? 'p' : 'a';
  const h12 = ((h + 11) % 12) + 1;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
}

// -----------------------------------------------------------------------------
// Eastern Time (America/New_York)
// -----------------------------------------------------------------------------
function _partsET(now = new Date()) {
  const opts = {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  };
  const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(now);
  const map = {};
  for (const p of parts) map[p.type] = p.value;
  // Some Intl impls emit hour "24" at midnight; normalize.
  if (map.hour === '24') map.hour = '00';
  return map;
}

// 'YYYY-MM-DD' in ET
export function nowEasternDate() {
  const p = _partsET();
  return `${p.year}-${p.month}-${p.day}`;
}

// MedTrack "logical day" — the day rolls over at 4:00 AM ET instead of
// midnight, so meds taken between 12:00–3:59 AM still belong to the
// previous day's card (e.g. Bedtime Meds at 2:00 AM logs to Monday
// when the wall clock says Tuesday). Used everywhere the app needs to
// decide which day is "today" — the Daily View live row, the calendar
// yellow highlight, missed / fully-taken bucketing, and the day-flip
// tick. The wall-clock display at the top of the page still shows
// real ET time; only the DATE identity is shifted.
const LOGICAL_DAY_ROLLOVER_HOUR = 4;

export function logicalTodayIso() {
  const p = _partsET();
  const hour = parseInt(p.hour, 10);
  if (hour >= LOGICAL_DAY_ROLLOVER_HOUR) {
    return `${p.year}-${p.month}-${p.day}`;
  }
  // Before 4 AM — logical day is still yesterday. Build a Date from
  // ET parts, subtract one day, and re-format as ISO. Constructing a
  // local Date with those y/m/d parts then subtracting a day is safe
  // because we only care about the calendar-day arithmetic, not the
  // absolute instant.
  const d = new Date(
    parseInt(p.year, 10),
    parseInt(p.month, 10) - 1,
    parseInt(p.day, 10),
  );
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// 'HH:MM' 24h in ET
export function nowEasternHHMM() {
  const p = _partsET();
  return `${p.hour}:${p.minute}`;
}

// Pretty ET clock string, e.g. "1:07 PM" (no timezone label per owner
// preference — the page is Eastern-only by design).
export function nowEasternPretty() {
  return fmt12(nowEasternHHMM());
}

// Pretty ET date string in month/day/year format, e.g. "9/21/2026"
// (no leading zeros on month or day, per owner preference). Uses the
// MedTrack logical day (4 AM rollover) so the header date agrees with
// the top card between midnight and 4 AM.
export function nowEasternDatePretty() {
  const iso = logicalTodayIso();
  const [y, m, d] = iso.split('-').map(Number);
  return `${m}/${d}/${y}`;
}

// -----------------------------------------------------------------------------
// Ideal-time computation
// -----------------------------------------------------------------------------
/**
 * Live ideal times for a day.
 *
 * Cascade rule:
 *   morning   = 1:00 PM (fixed)
 *   afternoon = (morning.taken_time  ?? morning ideal)    + 5.5h
 *   night     = (afternoon.taken_time ?? afternoon ideal) + 5.5h
 *   bedtime   = 2:00 AM (fixed)
 *
 * Freeze rule: once a slot's checkbox is ON, its ideal is locked to
 * whatever `slot.ideal_time` was snapshotted at the moment of check.
 * Upstream edits never shift a slot the user has already confirmed
 * as taken.
 */
export function computeIdeals(day) {
  // Morning — fixed unless the user has frozen it.
  const morningIdeal = day?.morning?.ideal_time || MORNING_IDEAL;

  // Afternoon.
  let afternoonIdeal;
  if (day?.afternoon?.checked && day?.afternoon?.ideal_time) {
    afternoonIdeal = day.afternoon.ideal_time;
  } else {
    const morningBase = day?.morning?.taken_time || morningIdeal;
    afternoonIdeal = fmtHHMM(parseHHMM(morningBase) + CASCADE_MINUTES);
  }

  // Night.
  let nightIdeal;
  if (day?.night?.checked && day?.night?.ideal_time) {
    nightIdeal = day.night.ideal_time;
  } else {
    const afternoonBase = day?.afternoon?.taken_time || afternoonIdeal;
    nightIdeal = fmtHHMM(parseHHMM(afternoonBase) + CASCADE_MINUTES);
  }

  // Bedtime — fixed unless the user has frozen it.
  const bedtimeIdeal = day?.bedtime?.ideal_time || BEDTIME_IDEAL;

  return {
    morning: morningIdeal,
    afternoon: afternoonIdeal,
    night: nightIdeal,
    bedtime: bedtimeIdeal,
  };
}

// True when every checkbox has been checked for this day.
export function isFullyTaken(day) {
  if (!day) return false;
  return SLOTS.every((s) => !!day[s.key]?.checked);
}

/**
 * One-time migration for legacy rows that were saved before the freeze
 * feature existed. Any slot that is checked but has no ideal_time gets
 * the currently-derived ideal snapshotted in. Runs on load; the caller
 * should persist the result if `changed` is true so the freeze sticks
 * across reloads.
 */
export function migrateFreeze(day) {
  const ideals = computeIdeals(day);
  let changed = false;
  const next = { ...day };
  for (const s of SLOTS) {
    const slot = next[s.key];
    if (slot && slot.checked && !slot.ideal_time) {
      next[s.key] = { ...slot, ideal_time: ideals[s.key] };
      changed = true;
    }
  }
  return { day: next, changed };
}

// -----------------------------------------------------------------------------
// API client
// -----------------------------------------------------------------------------
export async function fetchMedDays(start, end) {
  const { data } = await axios.get(`${API}/med/days`, {
    params: { start, end },
    withCredentials: true,
  });
  return data?.days || [];
}

export async function upsertMedDay(date, payload) {
  const body = { note: payload?.note || '' };
  for (const s of SLOTS) {
    body[s.key] = {
      checked: !!payload[s.key]?.checked,
      taken_time: payload[s.key]?.taken_time || null,
      ideal_time: payload[s.key]?.ideal_time || null,
    };
  }
  const { data } = await axios.put(`${API}/med/days/${date}`, body, {
    withCredentials: true,
  });
  return data?.day || null;
}

// ---------------------------------------------------------------------
// Oct 2026 — never-lose-a-check safety net.
// Every edit is mirrored into localStorage the moment it is made (before
// the debounced PUT fires) and only removed once the server confirms the
// save. If the tab is closed early, the network drops, or the sign-in
// session has expired, the checks survive on this device and are replayed
// on the next load. Keyed per user so an admin viewing the page never
// replays the patient's rows under their own account.
// ---------------------------------------------------------------------
const BACKUP_PREFIX = 'medtrack:unsaved:';

export function readBackup(userId) {
  try {
    const raw = localStorage.getItem(BACKUP_PREFIX + userId);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_e) {
    return {};
  }
}

export function writeBackup(userId, iso, doc) {
  try {
    const all = readBackup(userId);
    all[iso] = { ...doc, date: iso, saved_at: new Date().toISOString() };
    localStorage.setItem(BACKUP_PREFIX + userId, JSON.stringify(all));
  } catch (_e) { /* storage full / private mode — best effort */ }
}

export function clearBackup(userId, iso) {
  try {
    const all = readBackup(userId);
    if (!(iso in all)) return;
    delete all[iso];
    if (Object.keys(all).length) localStorage.setItem(BACKUP_PREFIX + userId, JSON.stringify(all));
    else localStorage.removeItem(BACKUP_PREFIX + userId);
  } catch (_e) { /* noop */ }
}

// Touches /auth/me, which slides the Google session + cookie out another
// 7 days. Resolves true when signed in, false when the session is gone.
export async function keepSessionAlive() {
  try {
    await axios.get(`${API}/auth/me`, { withCredentials: true });
    return true;
  } catch (e) {
    return e?.response?.status !== 401 && e?.response?.status !== 403;
  }
}
